import { useCallback, useEffect, useMemo, useState } from 'react'
import { Activity, Database, ExternalLink, RefreshCw, Search, ShieldCheck, TimerReset } from 'lucide-react'
import { buildScraperCoverageRows, fetchScraperCoverageBundle, summarizeScraperCoverage } from '../lib/scraperCoverageData'
import './ScraperCoverageWidget.css'

function formatCount(value) {
  return Number(value || 0).toLocaleString()
}

function formatAge(ms) {
  if (ms == null) return 'unknown'
  if (ms < 60000) return `${Math.floor(ms / 1000)}s`
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m`
  return `${Math.floor(ms / 3600000)}h`
}

function formatTimestamp(value) {
  if (!value) return 'never'
  return new Date(value).toLocaleString()
}

function sourceTitle(source) {
  const parts = [source.status]
  if (source.latencySeconds != null) parts.push(`${source.latencySeconds.toFixed(2)}s`)
  if (source.rawBytes) parts.push(`${source.rawBytes.toLocaleString()} bytes`)
  if (source.checkedAt) parts.push(formatTimestamp(source.checkedAt))
  return parts.join(' | ')
}

function StateBadge({ state, children }) {
  return <span className={`scw-state scw-state-${state}`}>{children || state}</span>
}

export default function ScraperCoverageWidget() {
  const [bundle, setBundle] = useState(null)
  const [errors, setErrors] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('attention')
  const [nowMs, setNowMs] = useState(Date.now())

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchScraperCoverageBundle()
      setBundle(result.bundle)
      setErrors(result.errors)
      setError(null)
      setNowMs(Date.now())
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
    const refreshId = setInterval(refresh, 60000)
    const clockId = setInterval(() => setNowMs(Date.now()), 1000)
    return () => {
      clearInterval(refreshId)
      clearInterval(clockId)
    }
  }, [refresh])

  const rows = useMemo(() => buildScraperCoverageRows(bundle, nowMs), [bundle, nowMs])
  const summary = useMemo(() => summarizeScraperCoverage(bundle, rows, nowMs), [bundle, rows, nowMs])
  const filteredRows = useMemo(() => rows.filter((row) => {
    if (search && !`${row.display} ${row.key}`.toLowerCase().includes(search.toLowerCase())) return false
    if (filter === 'source-gaps') return row.sourceCount < 4
    if (filter === 'not-exhaustive') return !row.exhaustiveVerified
    if (filter === 'not-joined') return !row.joined
    if (filter === 'orphans') return row.orphanOffers > 0
    if (filter === 'continuous') return row.continuousState !== 'running'
    if (filter === 'attention') {
      return row.sourceCount < 4 || !row.exhaustiveVerified || !row.joined || row.continuousState !== 'running'
    }
    return true
  }), [rows, search, filter])

  return (
    <div className="widget scw-widget">
      <div className="scw-hero">
        <div>
          <div className="scw-eyebrow">CANONICAL SPORTSBOOK UNIVERSE + LIVE ODDS READINESS</div>
          <h2>Sportsbook Universe, Scraper Coverage & Marriage</h2>
          <p>The 251-book master universe, built scraper fleet, four-source proof, exhaustive capture, continuous cadence, and joined-offer coverage are separate gates. A green source does not certify the other gates.</p>
        </div>
        <button className="scw-refresh" onClick={refresh} disabled={loading}>
          <RefreshCw size={15} className={loading ? 'scw-spin' : ''} /> Refresh truth
        </button>
      </div>

      <div className="scw-summary">
        <div className="scw-metric"><strong>{summary.masterBooks || '—'}</strong><span>master universe</span></div>
        <div className="scw-metric"><strong>{summary.masterActiveBooks || '—'}</strong><span>active master books</span></div>
        <div className="scw-metric"><strong>{summary.masterBuiltBooks || '—'}</strong><span>marked built in master</span></div>
        <div className="scw-metric"><strong>{summary.registeredBooks}</strong><span>registered proof fleet</span></div>
        <div className="scw-metric"><strong>{summary.anyLiveBooks}/{summary.activeBooks}</strong><span>books with live source</span></div>
        <div className="scw-metric"><strong>{summary.okSourceCells}/{summary.expectedSourceCells}</strong><span>source cells passing</span></div>
        <div className="scw-metric"><strong>{summary.joinedBooks}/{summary.activeBooks}</strong><span>books in joined release</span></div>
        <div className="scw-metric scw-metric-danger"><strong>{summary.exhaustiveBooks}</strong><span>exhaustive depth certified</span></div>
        <div className="scw-metric scw-metric-danger"><strong>{summary.continuousBooks}</strong><span>continuous cadence certified</span></div>
      </div>

      <div className="scw-truth-strip">
        <div><TimerReset size={14} /> Joined release age <strong>{formatAge(summary.releaseAgeMs)}</strong></div>
        <div><Database size={14} /> {formatCount(summary.totalOffers)} captured offers</div>
        <div><Activity size={14} /> {formatCount(summary.orphanOffers)} orphan offers</div>
        <div><ShieldCheck size={14} /> Updated {formatTimestamp(summary.releaseCapturedAt)}</div>
      </div>

      <div className="scw-links">
        <a href="https://sportsbook-master-list.vercel.app/" target="_blank" rel="noreferrer">Canonical 251-book master list <ExternalLink size={12} /></a>
        <a href="https://abet-scraper-proof-portal.vercel.app/all-scrapers-proof" target="_blank" rel="noreferrer">Run source proofs <ExternalLink size={12} /></a>
        <a href="https://abet-scraper-proof-portal.vercel.app/crossbook-inventory" target="_blank" rel="noreferrer">Raw offer inventory <ExternalLink size={12} /></a>
        <a href="https://abet-scraper-proof-portal.vercel.app/crossbook-allbets" target="_blank" rel="noreferrer">Married offer tables <ExternalLink size={12} /></a>
        <a href="https://abet-scraper-proof-portal.vercel.app/data/marriage_integrity.json" target="_blank" rel="noreferrer">Marriage integrity JSON <ExternalLink size={12} /></a>
      </div>

      <div className="scw-warning">
        Continuous controls are intentionally not faked: no per-book scheduler receipt/control API is published yet. Until that contract exists, every book remains continuous-UNVERIFIED even if it appears in a stale joined release.
      </div>
      {errors.length > 0 && <div className="scw-partial">Partial artifact load: {errors.join(' | ')}</div>}
      {error && <div className="scw-error">{error}</div>}

      <div className="scw-toolbar">
        <label className="scw-search"><Search size={14} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Find sportsbook" /></label>
        <select value={filter} onChange={(event) => setFilter(event.target.value)}>
          <option value="attention">Needs attention</option>
          <option value="all">All books</option>
          <option value="source-gaps">Source gaps</option>
          <option value="not-exhaustive">Not exhaustive</option>
          <option value="not-joined">Not joined</option>
          <option value="orphans">Has orphan offers</option>
          <option value="continuous">Continuous unverified</option>
        </select>
        <span>{filteredRows.length} of {summary.registeredBooks} registered scraper rows</span>
      </div>

      <div className="scw-table-wrap">
        <table className="scw-table">
          <thead><tr><th>Sportsbook</th><th>Sources</th><th>On demand</th><th>Exhaustive</th><th>Captured</th><th>Married</th><th>Continuous</th><th>Proof</th></tr></thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr key={row.key}>
                <td data-label="Sportsbook"><strong>{row.display}</strong><small>{row.key}</small></td>
                <td data-label="Sources"><div className="scw-sources">{row.sourceCells.map((source) => (
                  <span key={source.key} className={source.ok ? 'is-ok' : 'is-fail'} title={sourceTitle(source)}>{source.label}</span>
                ))}</div></td>
                <td data-label="On demand"><StateBadge state={row.onDemandState}>{row.sourceCount}/4</StateBadge></td>
                <td data-label="Exhaustive"><StateBadge state={row.exhaustiveVerified ? 'verified' : 'unverified'}>{row.exhaustiveVerified ? 'VERIFIED' : 'UNVERIFIED'}</StateBadge></td>
                <td data-label="Captured"><strong>{formatCount(row.capturedOffers)}</strong><small>{formatCount(row.capturedMarkets)} market types</small></td>
                <td data-label="Married">{row.joined ? <><strong>{formatCount(row.joinedOffers)}</strong><small>{row.matchRate}% match / {formatCount(row.orphanOffers)} orphan</small></> : <StateBadge state="not-in-release">NOT IN RELEASE</StateBadge>}</td>
                <td data-label="Continuous"><StateBadge state={row.continuousState}>{row.continuousState.toUpperCase()}</StateBadge></td>
                <td data-label="Proof"><a className="scw-proof" href={row.proofUrl} target="_blank" rel="noreferrer">Open <ExternalLink size={11} /></a></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
